import { useCallback, useMemo } from "react";
import {
  getAttributeBooleanValue,
  getAttributeValue,
  getProductSku,
} from "../helpers";
import type { NavigateFunction } from "react-router";
import type { GtmDataLayerEvent } from "@made-people/grease-gtm";
import type {
  Product,
  ProductBase,
  ProductPurchaseInfo,
} from "@made-people/grease-product";
import type { CustomisationFeature } from "../settings";

type ComfortSuiteTrackingProduct = {
  name: string | undefined;
  sku: string | undefined;
  url: string;
  toggleValue: string | undefined;
};

type ComfortSuiteTrackingEntry = {
  source_product: ComfortSuiteTrackingProduct;
  target_product: ComfortSuiteTrackingProduct;
};

export const useCustomisationTracking = (params: {
  href: (to: string) => string;
  navigate: NavigateFunction;
  pushCustomEvent: (data: GtmDataLayerEvent) => void;
  productBase: ProductBase;
  purchaseInfo: ProductPurchaseInfo;
  customisationFeatures: CustomisationFeature[];
  targetProduct: Product | undefined;
  sourceUrl: string;
  currentUrl: string;
  onClose: () => void;
}) => {
  const getProductTrackingData = useCallback(
    (
      product: Product | undefined,
      feature: CustomisationFeature,
    ):
      | {
          name: string | undefined;
          sku: string | undefined;
          url: string;
          toggleValue: string | undefined;
        }
      | undefined => {
      if (!product || !feature.trackingKey || !feature.toggleValues) {
        return undefined;
      }

      const toggleValues = feature.toggleValues
        .split(",")
        .map((value) => value.trim());
      const booleanValue = getAttributeBooleanValue(
        product.base,
        feature.trackingKey,
      );
      const toggleValue =
        toggleValues.length >= 2
          ? booleanValue
            ? toggleValues[0]
            : toggleValues[1]
          : undefined;

      return {
        name: product.base.content.name,
        sku: getProductSku(product),
        url: params.href(product.base.slug),
        toggleValue,
      };
    },
    [params.href],
  );

  const trackData = useMemo<ComfortSuiteTrackingEntry[]>(() => {
    const targetProduct = params.targetProduct;
    if (!targetProduct) {
      return [];
    }

    const entries: ComfortSuiteTrackingEntry[] = [];
    for (const feature of params.customisationFeatures) {
      if (!feature.trackingKey || !feature.toggleValues) {
        continue;
      }

      const sourceValue = getAttributeValue(
        params.productBase,
        feature.trackingKey,
      );
      const targetValue = getAttributeValue(
        targetProduct.base,
        feature.trackingKey,
      );
      if (sourceValue === targetValue) {
        continue;
      }

      const sourceProductData = {
        name: params.productBase.content.name,
        sku: params.purchaseInfo.sku,
        url: params.sourceUrl,
        toggleValue: getAttributeBooleanValue(
          params.productBase,
          feature.trackingKey,
        )
          ? feature.toggleValues.split(",")[0]?.trim()
          : feature.toggleValues.split(",")[1]?.trim(),
      };

      const targetProductData = getProductTrackingData(targetProduct, feature);
      if (!targetProductData) {
        continue;
      }

      entries.push({
        source_product: sourceProductData,
        target_product: targetProductData,
      });
    }

    return entries;
  }, [
    getProductTrackingData,
    params.customisationFeatures,
    params.productBase,
    params.purchaseInfo.sku,
    params.sourceUrl,
    params.targetProduct,
  ]);

  const handleSubmit = useCallback(() => {
    if (params.currentUrl === params.sourceUrl) {
      params.onClose();
      return;
    }

    trackData.forEach((feature) => {
      params.pushCustomEvent({
        event: "comfort_suite_click",
        comfortSuiteClickEvent: {
          products: feature,
          context: "CustomisationPopup",
        },
      });
    });

    params.navigate(params.currentUrl);
    params.onClose();
  }, [params, trackData]);

  return { handleSubmit };
};
